import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { PencilIcon, CheckIcon } from '@heroicons/react/24/outline';
import HomeContainer from './components/HomeContainer';
import PageBanner from '../../components/utils/PageBanner';
import { useProject } from '../../config/ProjectContext';
import useAnalytics from '../../hooks/useAnalytics';

/**
 * Home Screen
 * 
 * Main landing page after sign-in displaying project-level widgets with
 * customizable layout and edit mode capabilities.
 * 
 * Requirements: All US-1 through US-5
 */
const Home = () => {
  const { project } = useProject();
  const { setAnalytics } = useAnalytics();
  const [isEditMode, setIsEditMode] = useState(false);

  // Track page view
  useEffect(() => {
    setAnalytics({
      event: 'HomePageViewed',
      sectionId: 'home',
      extraParams: {
        projectCode: project?.code,
        projectName: project?.name,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.code]);

  /**
   * Toggle edit mode
   */
  const handleToggleEditMode = () => {
    const newMode = !isEditMode;
    setIsEditMode(newMode);
    
    if (!newMode) {
      // Exiting edit mode
      setAnalytics({
        event: 'HomePageLayoutUpdated',
        sectionId: 'home',
        extraParams: { actionType: 'finish-editing' },
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Banner */}
      <PageBanner 
        title="Home"
        buttons={[isEditMode ? 'Exit Edit Mode' : 'Edit Home']}
        buttonIcons={[isEditMode ? <CheckIcon className="h-4 w-4" /> : <PencilIcon className="h-4 w-4" />]}
        buttonDisabled={[false]}
        onClicks={[handleToggleEditMode]}
      />

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-6 py-6">
        {/* No Project Warning */}
        {!project?.code && (
          <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 text-sm">
              <strong>Note:</strong> Please select a project from the header to see project-specific widgets.
            </p>
          </div>
        )}

        {/* Home Container with Widgets */}
        <HomeContainer 
          isEditMode={isEditMode}
          onEditModeChange={setIsEditMode}
        />
      </main>
    </div>
  );
};

Home.propTypes = {
  openSnackBar: PropTypes.func,
};

export default Home;
